import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { LENS_LAYOUT } from '~/configs/unifiedAnalyticsTheme';
import { nativeWorkspaceParams } from '~/nativeLenses/nativeWorkspaceNavigation';

import { Box, useTheme } from '@mui/material';

import Footer from '~/components/Common/Footer';

import { openCertificationRequests } from '~/pages/LeLens/catalogue/LensCertificationQueue';
import NativePersonalViewBoundary from '~/pages/LeLens/personalViews/NativePersonalViewBoundary';

import { useGetReportingLeLenses } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { useGetLensCertificationRequests } from '~/hooks/apis/reporting/useLensCertification';
import { useGetBrowsableLensSpaces, useGetLensSpaces } from '~/hooks/apis/reporting/useLensSpaces';

import Rail from '../Rail';
import { useViewportWidth } from '../useRailCollapsed';

import PageChat from './PageChat';
import { OpenPageChat } from './PageChatButton';
import { type PageBridge, PageBridgeRegistration } from './pageBridge';

export default function PageChatLayout({
  children,
  navigationHeight = 0,
}: {
  children: React.ReactNode;
  navigationHeight?: number;
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const shell = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const header = document.querySelector('header, [role="banner"]');
    const update = () => {
      const top = Math.max(0, header?.getBoundingClientRect().bottom ?? 0);
      shell.current?.style.setProperty('--page-chat-top', `${top}px`);
    };
    update();
    window.addEventListener('resize', update);
    const observer = new ResizeObserver(update);
    if (header) observer.observe(header);
    return () => {
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, []);
  const viewportWidth = useViewportWidth();
  // Data apps start with more room for their canvas, independently of the main askRichard preference.
  const [railCollapsed, setRailCollapsed] = useState(true);
  const toggleRail = () => setRailCollapsed((collapsed) => !collapsed);
  const railWidth = railCollapsed ? 60 : LENS_LAYOUT.railWidth;
  // The Tours tabs sit outside this fixed shell. Both must move with the same rail.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--tours-page-rail-width', `${railWidth}px`);
    // A tab strip scrolled at a narrower width should start at the first tab after resizing the rail.
    document.querySelector('[aria-label="Tours navigation"]')?.scrollTo({ left: 0 });
    return () => {
      document.documentElement.style.removeProperty('--tours-page-rail-width');
    };
  }, [railWidth]);
  const lenses = useGetReportingLeLenses();
  const { data: myFolders = [] } = useGetLensSpaces();
  const browsableFolders = useGetBrowsableLensSpaces();
  const canReviewCertification = Boolean(browsableFolders.data?.libraryAccess?.canReviewCertification);
  const { data: certificationRequests = [] } = useGetLensCertificationRequests(canReviewCertification);
  const go = (values: Record<string, string>) => {
    const params = nativeWorkspaceParams(
      window.location.pathname,
      window.location.search,
      values.view === 'lens' ? values.lens : undefined,
    );
    Object.entries(values).forEach(([key, value]) => params.set(key, value));
    navigate(`/ask-richard-unified?${params}`);
  };
  const [bridge, setBridge] = useState<PageBridge | null>(null);
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(() =>
    Math.max(320, Math.min(Number(sessionStorage.getItem('askrichard.page.width')) || 420, window.innerWidth * 0.65)),
  );
  useLayoutEffect(() => {
    const chatWidth = open && viewportWidth >= theme.breakpoints.values.lg ? width : 0;
    document.documentElement.style.setProperty('--tours-page-chat-width', `${chatWidth}px`);
    return () => {
      document.documentElement.style.removeProperty('--tours-page-chat-width');
    };
  }, [open, width, viewportWidth, theme.breakpoints.values.lg]);
  const drag = useRef<{ x: number; width: number } | null>(null);
  const resize = (next: number) => {
    const bounded = Math.round(Math.max(320, Math.min(next, window.innerWidth * 0.65)));
    setWidth(bounded);
    sessionStorage.setItem('askrichard.page.width', String(bounded));
  };
  return (
    <PageBridgeRegistration.Provider value={setBridge}>
      <OpenPageChat.Provider value={() => setOpen(true)}>
        <Box ref={shell}>
          <Box sx={{ position: 'fixed', left: 0, top: 'var(--page-chat-top, 64px)', bottom: 0, zIndex: 1099 }}>
            <Rail
              collapsed={railCollapsed}
              onToggleCollapsed={toggleRail}
              view={null}
              activeThreadId={null}
              activeLensId={null}
              onNavigate={(view, id) => go({ view, ...(id ? { [view === 'lens' ? 'lens' : 'thread']: id } : {}) })}
              onNewThread={() => go({ view: 'thread' })}
              onNewLens={() => go({ view: 'lensIndex', create: '1' })}
              onOpenArea={(area) => go({ view: 'area', area })}
              onOpenCommercialAnalysis={(analysis) => go({ view: 'catalogue', domain: 'commercial', analysis })}
              onOpenGrowthPage={(growthPage) => go({ view: 'catalogue', domain: 'growth', growthPage })}
              onOpenAccommodationModel={(accommodationModel) =>
                go({ view: 'catalogue', domain: 'commercial', analysis: 'product-accommodation', accommodationModel })
              }
              onOpenDataCatalogue={() => go({ view: 'catalogue', section: 'data' })}
              realLenses={lenses.data ?? []}
              realLensesStatus={lenses.status}
              realLensesError={lenses.error}
              onRetryLenses={() => void lenses.refetch()}
              folders={browsableFolders.data?.spaces ?? myFolders}
              canReviewCertification={canReviewCertification}
              certificationRequestCount={openCertificationRequests(certificationRequests).length}
              onOpenLibrary={(destination) =>
                go({ view: 'lensIndex', ...(destination === 'home' ? {} : { library: destination }) })
              }
            />
          </Box>
          <Box
            aria-label="Page canvas"
            sx={{
              bgcolor: 'background.default',
              position: 'fixed',
              top: `calc(var(--page-chat-top, 64px) + ${navigationHeight}px)`,
              left: railWidth,
              right: { xs: 0, lg: open ? width : 0 },
              bottom: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              overscrollBehavior: 'none',
              overflowAnchor: 'none',
            }}
          >
            <Box sx={{ flex: '1 0 auto', minWidth: 0 }}>
              <NativePersonalViewBoundary>{children}</NativePersonalViewBoundary>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <Footer maxWidth={false} />
            </Box>
          </Box>
          {bridge && (
            <Box sx={{ visibility: open ? 'visible' : 'hidden', pointerEvents: open ? 'auto' : 'none' }}>
              <PageChat bridge={bridge} width={`${width}px`} onClose={() => setOpen(false)} />
              <Box
                role="separator"
                aria-label="Resize chat"
                aria-orientation="vertical"
                aria-valuenow={width}
                aria-valuemin={320}
                aria-valuemax={Math.floor(window.innerWidth * 0.65)}
                tabIndex={0}
                onPointerDown={(event) => {
                  event.preventDefault();
                  drag.current = { x: event.clientX, width };
                  event.currentTarget.setPointerCapture(event.pointerId);
                }}
                onPointerMove={(event) => {
                  if (!drag.current) return;
                  const next = drag.current.width + drag.current.x - event.clientX;
                  if (next < 280) {
                    drag.current = null;
                    event.currentTarget.releasePointerCapture(event.pointerId);
                    setOpen(false);
                  } else resize(next);
                }}
                onPointerUp={(event) => {
                  drag.current = null;
                  event.currentTarget.releasePointerCapture(event.pointerId);
                }}
                onPointerCancel={() => {
                  drag.current = null;
                }}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                    event.preventDefault();
                    resize(width + (event.key === 'ArrowLeft' ? 24 : -24));
                  }
                }}
                sx={{
                  display: { xs: 'none', lg: 'block' },
                  position: 'fixed',
                  right: width - 4,
                  width: 8,
                  top: 'var(--page-chat-top, 64px)',
                  bottom: 0,
                  zIndex: 1101,
                  cursor: 'col-resize',
                  touchAction: 'none',
                  '&:hover, &:focus-visible': { bgcolor: 'rgba(15, 70, 79, 0.12)', outline: 'none' },
                }}
              />
            </Box>
          )}
        </Box>
      </OpenPageChat.Provider>
    </PageBridgeRegistration.Provider>
  );
}
